"use client";

import { cn } from "@guru/shared/utils/cn";
import { motion, useReducedMotion } from "motion/react";

/**
 * Animated per-tier illustration for the pricing cards. The whole pricing
 * model is "pick your monthly WhatsApp volume", so each card's hero image is
 * that allowance filling up: a WhatsApp bubble streams messages into a meter
 * that fills to the tier's share of capacity. Lite fills partway, Scale fills
 * fully with buyable extras spilling over, Custom runs to infinity.
 *
 * Motion is always-on but gentle; under prefers-reduced-motion every scene
 * resolves to a clean static frame. Decorative only — the card supplies the
 * real numbers and accessible labels.
 */

const INK = "var(--brand-navy)";
const SAFFRON = "var(--brand-saffron)";
const POSITIVE = "var(--brand-positive)";

export type MeterVariant = "lite" | "scale" | "custom";

/** Visual share of the gauge each tier fills (Scale 1,500 msgs = full bar). */
const FILL_FRACTION: Record<MeterVariant, number> = {
  lite: 0.47,
  scale: 1,
  custom: 1,
};

const toneFrame: Record<MeterVariant, string> = {
  lite: "border-border bg-gradient-to-br from-ink/[0.05] via-card to-card",
  scale: "border-brand/30 bg-gradient-to-br from-brand/[0.1] via-card to-card",
  custom: "border-ink/25 bg-gradient-to-br from-ink/[0.07] via-card to-card",
};

const toneGlow: Record<MeterVariant, string> = {
  lite: "bg-ink/15",
  scale: "bg-brand/35",
  custom: "bg-ink/25",
};

const TRACK_X = 30;
const TRACK_W = 180;
const FILL_PAD = 2;
const FILL_W = TRACK_W - FILL_PAD * 2;

interface AllowanceMeterProps {
  variant: MeterVariant;
  className?: string;
}

export function AllowanceMeter({ variant, className }: AllowanceMeterProps) {
  const reduced = !!useReducedMotion();
  const fraction = FILL_FRACTION[variant];
  const fillWidth = FILL_W * fraction;

  // Ambient loop: infinite under normal motion, an instant no-op when reduced.
  const loop = (duration: number, delay = 0, repeatDelay = 0) =>
    reduced
      ? { duration: 0 }
      : {
          duration,
          delay,
          repeat: Number.POSITIVE_INFINITY,
          repeatDelay,
          ease: "easeInOut" as const,
        };

  return (
    <div
      className={cn(
        "relative isolate h-28 w-full overflow-hidden rounded-xl border",
        toneFrame[variant],
        className
      )}
    >
      <div className="ledger-grid absolute inset-0 opacity-50" aria-hidden="true" />
      <div
        className={cn(
          "absolute -left-8 -top-10 h-28 w-28 rounded-full blur-2xl",
          toneGlow[variant]
        )}
        aria-hidden="true"
      />

      <svg
        viewBox="0 0 240 120"
        className="relative z-10 h-full w-full"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        {/* WhatsApp source bubble — the messages come from here */}
        <g>
          <path
            d="M34 22 h60 a10 10 0 0 1 10 10 v16 a10 10 0 0 1 -10 10 h-48 l-12 9 v-9 h0 a10 10 0 0 1 -10 -10 v-16 a10 10 0 0 1 10 -10 z"
            fill="#dcf8c6"
            stroke={POSITIVE}
            strokeOpacity="0.35"
            strokeWidth="1.4"
          />
          <rect x="44" y="32" width="44" height="5" rx="2.5" fill={INK} opacity="0.28" />
          <rect x="44" y="42" width="30" height="5" rx="2.5" fill={INK} opacity="0.16" />
        </g>

        {/* Messages streaming from the bubble into the meter */}
        {[
          { x: 70, delay: 0 },
          { x: 118, delay: 0.5 },
          { x: 166, delay: 1 },
        ].map((dot) => (
          <motion.circle
            key={dot.x}
            cx={dot.x}
            r="4"
            fill={SAFFRON}
            initial={false}
            animate={reduced ? { cy: 80, opacity: 0 } : { cy: [56, 80], opacity: [0, 1, 0] }}
            transition={loop(1.8, dot.delay, 0.6)}
          />
        ))}

        {/* Gauge track */}
        <rect
          x={TRACK_X}
          y="80"
          width={TRACK_W}
          height="22"
          rx="11"
          fill={INK}
          fillOpacity="0.07"
          stroke={INK}
          strokeOpacity="0.18"
          strokeWidth="1.5"
        />
        {/* Quarter ticks */}
        {[0.25, 0.5, 0.75].map((t) => (
          <line
            key={t}
            x1={TRACK_X + TRACK_W * t}
            y1="84"
            x2={TRACK_X + TRACK_W * t}
            y2="98"
            stroke={INK}
            strokeOpacity="0.12"
            strokeWidth="1"
          />
        ))}

        {/* Animated fill — grows from empty to the tier's share on view */}
        <motion.rect
          x={TRACK_X + FILL_PAD}
          y="82"
          height="18"
          rx="9"
          fill={SAFFRON}
          initial={false}
          animate={reduced ? { width: fillWidth } : { width: [0, fillWidth] }}
          transition={
            reduced ? { duration: 0 } : { duration: 1.1, delay: 0.2, ease: [0.34, 1.2, 0.64, 1] }
          }
        />

        {/* Scale: buyable extras spilling over the full bar */}
        {variant === "scale" &&
          [
            { x: 196, delay: 1.3 },
            { x: 210, delay: 1.6 },
          ].map((chip) => (
            <motion.g
              key={chip.x}
              initial={false}
              animate={
                reduced ? { scale: 1, opacity: 1 } : { scale: [0, 1.15, 1], opacity: [0, 1] }
              }
              transition={loop(2.4, chip.delay, 0.8)}
              style={{ transformOrigin: `${chip.x}px 66px` }}
            >
              <circle cx={chip.x} cy="66" r="8" fill={SAFFRON} />
              <path
                d={`M${chip.x - 3.5} 66 h7 M${chip.x} 62.5 v7`}
                stroke="var(--brand-saffron-contrast)"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </motion.g>
          ))}

        {/* Custom: the meter runs to infinity */}
        {variant === "custom" && (
          <motion.g
            initial={false}
            animate={reduced ? { opacity: 1 } : { opacity: [0.6, 1, 0.6] }}
            transition={loop(2.2)}
          >
            <path
              d="M196 91 a5 5 0 1 1 7 0 a5 5 0 1 0 7 0"
              fill="none"
              stroke="var(--brand-saffron-contrast)"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </motion.g>
        )}
      </svg>
    </div>
  );
}
